import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { Toaster } from 'sonner'
import { authClient } from '#/lib/auth-client'
import { clearSentryUser, setSentryUser } from '#/lib/sentry'

interface User {
  id: string
  name: string
  email: string
  image?: string | null
  role?: string
}

interface SignInParams {
  email: string
  password: string
}

interface AuthContextType {
  user: User | null
  loading: boolean
  signIn: (params: SignInParams) => Promise<{ success: boolean; error?: string }>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  signIn: async () => ({ success: false }),
  signOut: async () => {},
})

function FullPageSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  )
}

function mapUser(raw: unknown): User {
  const u = raw as Record<string, unknown>
  return {
    id: u.id as string,
    name: u.name as string,
    email: u.email as string,
    image: u.image as string | null | undefined,
    role: u.role as string | undefined,
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function initSession() {
      try {
        const { data } = await authClient.getSession()
        if (data?.user) {
          const mapped = mapUser(data.user)
          setUser(mapped)
          setSentryUser(mapped)
        }
      } catch {
        // Not authenticated
      } finally {
        setLoading(false)
      }
    }
    initSession()
  }, [])

  const handleSignIn = useCallback(async ({ email, password }: SignInParams) => {
    try {
      const result = await authClient.signIn.email({ email, password })
      if (result.error) {
        return { success: false, error: result.error.message || 'Sign in failed' }
      }
      if (result.data?.user) {
        const mapped = mapUser(result.data.user)
        setUser(mapped)
        setSentryUser(mapped)
      }
      return { success: true }
    } catch {
      return { success: false, error: 'An unexpected error occurred' }
    }
  }, [])

  const handleSignOut = useCallback(async () => {
    await authClient.signOut()
    setUser(null)
    clearSentryUser()
    window.location.href = '/'
  }, [])

  return (
    <AuthContext.Provider value={{ user, loading, signIn: handleSignIn, signOut: handleSignOut }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

/** Redirect to /dashboard if already authenticated */
export function RedirectIfAuthenticated({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth()

  if (loading) return <FullPageSpinner />
  if (user) return <Navigate to="/dashboard" replace />
  return <>{children}</>
}

/** Redirect to / if not authenticated */
export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth()
  const location = useLocation()

  if (loading) return <FullPageSpinner />
  if (!user) return <Navigate to="/" state={{ from: location }} replace />
  return (
    <>
      <Toaster position="top-right" richColors closeButton />
      {children}
    </>
  )
}
